/**
 * UnsupportedBrowserScreen Component
 *
 * Shown when WebHID is not available (e.g., Firefox, Safari).
 * Guides users to use a supported browser (Chrome or Edge).
 */

import { Button, CloseButton } from '@ui/shared/Button';
import * as React from 'react';
import styled from 'styled-components';
import { CardFooter } from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { Icon } from '../../../shared/Icon';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../../shared/popups/PopupCard';
import { KEYS } from '../../../../keys';

/**
 * Props for UnsupportedBrowserScreen
 */
export interface UnsupportedBrowserScreenProps {
  onClose: () => void;
}

/**
 * UnsupportedBrowserScreen component
 */
export const UnsupportedBrowserScreen: React.FC<
  UnsupportedBrowserScreenProps
> = ({ onClose }: UnsupportedBrowserScreenProps): React.ReactElement => {
  return (
    <InnerPopupCard onClose={onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <TitleSection>
              <Icon name='cloud_download' size='medium' />
              <HeaderTitle>Update your Hawkin Force Plates</HeaderTitle>
            </TitleSection>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton
              onClick={onClose}
              data-testid={KEYS.WIZARD_UNSUPPORTED_CLOSE}
            />
          </CardHeaderRight>
        </CardHeader>

        <UnsupportedContent data-testid={KEYS.WIZARD_UNSUPPORTED_SCREEN}>
          <UnsupportedIcon>
            <Icon name='browser_not_supported' size='large' color='white' />
          </UnsupportedIcon>
          <UnsupportedTitle data-testid={KEYS.WIZARD_UNSUPPORTED_TITLE}>
            Browser Not Supported
          </UnsupportedTitle>
          <UnsupportedDescription>
            Your current browser does not support the technology required to
            update your hardware directly from the web.
          </UnsupportedDescription>

          <BrowserRecommendation>
            <RecommendationTitle>
              Please use one of these browsers:
            </RecommendationTitle>
            <BrowserList>
              <BrowserItem data-testid={KEYS.WIZARD_BROWSER_CHROME}>
                <BrowserIcon>
                  <Icon name='public' size='medium' />
                </BrowserIcon>
                <BrowserInfo>
                  <BrowserName>Google Chrome</BrowserName>
                </BrowserInfo>
              </BrowserItem>
              <BrowserItem data-testid={KEYS.WIZARD_BROWSER_EDGE}>
                <BrowserIcon>
                  <Icon name='public' size='medium' />
                </BrowserIcon>
                <BrowserInfo>
                  <BrowserName>Microsoft Edge</BrowserName>
                </BrowserInfo>
              </BrowserItem>
            </BrowserList>
          </BrowserRecommendation>

          <HelpNote>
            <Icon name='info' size='small' />
            <span>
              Copy this page&#39;s URL and paste it in Chrome or Edge to
              continue with the update.
            </span>
          </HelpNote>
        </UnsupportedContent>

        <CardFooter surface={8}>
          <FooterContent>
            <LeftActions />
            <RightActions>
              <Button
                title='Close'
                flavor='secondary'
                onClick={onClose}
              />
            </RightActions>
          </FooterContent>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};

// Styled Components

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
`;

const HeaderTitle = styled.h5`
  margin: 0;
  font-weight: bold;
  color: var(--color-text);
`;

const FooterContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const LeftActions = styled.div`
  display: flex;
  gap: 10px;
`;

const RightActions = styled.div`
  display: flex;
  gap: 10px;
`;

const UnsupportedContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px;
  gap: 16px;
`;

const UnsupportedIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: var(--color-react-select-danger);
  border-radius: 50%;
  margin-bottom: 8px;
`;

const UnsupportedTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: var(--color-text);
`;

const UnsupportedDescription = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.72);
  max-width: 450px;
`;

const BrowserRecommendation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: var(--color-surface-1);
  border-radius: 10px;
  margin-top: 8px;
  width: 100%;
  max-width: 400px;
`;

const RecommendationTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
`;

const BrowserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BrowserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: var(--color-surface-2);
  border-radius: 8px;
`;

const BrowserIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--color-primary);
  border-radius: 8px;
  color: white;
`;

const BrowserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const BrowserName = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
`;

const HelpNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
  margin-top: 8px;
`;

export default UnsupportedBrowserScreen;
